import { Outlet, createFileRoute } from "@tanstack/react-router"
import { zodValidator } from "@tanstack/zod-adapter"
import { z } from "zod"

import CollectionDetailLayout from "~/components/demand-2/CollectionDetailLayout"
import ChooseSubCollection from "~/components/demand-2/shared/ChooseSubCollection"
import {
  CollectionNotFound,
  ensureCollectionExists,
  parseCollectionIdParams,
} from "~/components/collections/collectionRouteGuard"
import { STRATEGY_SECTION_KEYS } from "~/components/demand-2/shared/StrategySummaryRail"
import LoadingOverlay from "~/components/reusable/LoadingOverlay"
import NotFound from "~/components/reusable/NotFound"
import useShowHelix from "~/hooks/useShowHelix"
import { GROUP_DEMAND_PLANNING_ENABLED } from "~/utils/demandPlanningLevel"
import { reconciliationPeriodKeySchema } from "~/utils/reconciliationPeriod"

const searchSchema = z.object({
  // When true, $collectionId is a collection-group id and all tabs aggregate its members.
  isGroup: z.boolean().default(false),
  // Opens the demand strategy dialog; in the URL so it deep-links and Back closes it.
  strategy: z.boolean().optional(),
  // The dialog section to scroll to on arrival, as the rail does.
  strategySection: z.enum(STRATEGY_SECTION_KEYS).optional().catch(undefined),
  // Opens the Assortment tab's Add variants dialog on arrival.
  addVariants: z.boolean().optional(),
  // The Budget tab's period (`fy2026`, `next-52-weeks`); one no longer offered falls back.
  period: reconciliationPeriodKeySchema.optional().catch(undefined),
})

export const Route = createFileRoute("/_auth/demand/noos/$collectionId/_layout")({
  params: { parse: parseCollectionIdParams },
  validateSearch: zodValidator(searchSchema),
  loaderDeps: ({ search }) => ({ isGroup: search.isGroup }),
  loader: ({ params, deps, context }) =>
    ensureCollectionExists(context.trpcUtils, params.collectionId, deps.isGroup),
  pendingComponent: () => <LoadingOverlay />,
  notFoundComponent: CollectionNotFound,
  component: RouteComponent,
})

function RouteComponent() {
  const showHelix = useShowHelix()
  const { collectionId } = Route.useParams()
  const { isGroup } = Route.useSearch()
  if (!showHelix) return <NotFound />
  if (isGroup && !GROUP_DEMAND_PLANNING_ENABLED) return <ChooseSubCollection />

  return (
    <CollectionDetailLayout collectionId={Number(collectionId)} kind="noos">
      <Outlet />
    </CollectionDetailLayout>
  )
}
